import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import banner from '../assets/tpp-claims-banner.jpg';
import Layout from '../components/Layout';
import ProgressSteps from '../components/ProgressSteps';
import SubmitClaimConfirmDialog from '../components/SubmitClaimConfirmDialog';
import { RootState } from '../store/store';
import { config } from '../config';
import {
  deleteInventoryAttachment,
  deleteInventoryItem,
  getClaim,
  getInventoryItems,
  InventoryItem,
  resubmitIncompleteClaim,
  sendClaimConfirmation,
  updateClaim,
  uploadInventoryAttachments,
} from '../services/api';
import { useClaimTokenResume } from '../hooks/useClaimTokenResume';
import { buildClaimResumePath, claimStatusReturnPath } from '../utils/claimTokenResume';
import { useSnackbar } from '../contexts/SnackbarContext';
import { getErrorMessage } from '../utils/errorHandler';

const inventoryHeaderButtonSx = {
  borderColor: 'var(--primary-color)',
  color: 'var(--primary-color)',
  '&:hover': { borderColor: 'var(--primary-color)', backgroundColor: 'rgba(0, 30, 96, 0.04)' },
  px: 3,
  py: 0.9,
  borderRadius: 999,
  fontWeight: 700,
  fontSize: '0.78rem',
} as const;

const monthLabels: Record<string, string> = {
  '01': 'Jan',
  '02': 'Feb',
  '03': 'Mar',
  '04': 'Apr',
  '05': 'May',
  '06': 'Jun',
  '07': 'Jul',
  '08': 'Aug',
  '09': 'Sep',
  '10': 'Oct',
  '11': 'Nov',
  '12': 'Dec',
};

const isImageFilename = (filename: string): boolean => {
  const lower = filename.toLowerCase();
  return ['.png', '.jpg', '.jpeg', '.gif', '.webp'].some((ext) => lower.endsWith(ext));
};

const formatPurchase = (yyyyMm: string | null | undefined): string => {
  if (!yyyyMm) return 'Not provided';
  const [yyyy, mm] = String(yyyyMm).split('-');
  if (!yyyy || !mm) return String(yyyyMm);
  const mon = monthLabels[mm] || mm;
  return `${mon} ${yyyy}`;
};

const formatInventoryPrice = (price: string | number | null | undefined): string => {
  if (price === null || price === undefined || price === '') return 'N/A';
  const n = typeof price === 'number' ? price : Number(String(price).replace(/,/g, ''));
  if (Number.isNaN(n)) return 'N/A';
  return `$${n.toFixed(2)}`;
};

const formatClaimStatusLabel = (status: string | null | undefined): string => {
  const key = String(status ?? '').toUpperCase();
  const labels: Record<string, string> = {
    INCOMPLETE: 'Incomplete',
    SUBMITTED: 'Submitted',
    IN_REVIEW: 'In Review',
    PENDING_REVIEW: 'Pending Review',
    APPROVED: 'Approved',
    DENIED: 'Denied',
    DRAFT: 'Draft',
    PAID: 'Paid',
    CLOSED: 'Closed',
  };
  if (labels[key]) return labels[key];
  return key
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

type InventoryAttachment = NonNullable<InventoryItem['attachments']>[number];
type InventoryAttachmentType = 'inventory_evidence' | 'inventory_receipt';

const buildInventoryAttachmentPreviewUrl = (item: InventoryItem, att: InventoryAttachment): string =>
  `${config.API_URL}/claims/${item.tpp_claim_id}/inventory-attachments/${att.id}/preview?token=${encodeURIComponent(att.token)}`;

const fileExtensionLabel = (filename: string): string => {
  const base = filename.split(/[/\\]/).pop() || filename;
  const dot = base.lastIndexOf('.');
  if (dot <= 0 || dot === base.length - 1) return 'File';
  return base.slice(dot + 1).toUpperCase();
};

const attachmentTileSx = {
  width: { xs: 72, sm: 80 },
  height: { xs: 72, sm: 80 },
  flexShrink: 0,
  border: '1px solid var(--border-color)',
  borderRadius: 1,
  overflow: 'hidden',
  backgroundColor: '#fafafa',
} as const;

const attachmentAddButtonSx = {
  minWidth: 76,
  minHeight: 36,
  px: 1.5,
  py: 0.4,
  borderRadius: 999,
  fontWeight: 700,
  fontSize: '0.75rem',
  color: 'var(--primary-color)',
  borderColor: 'var(--primary-color)',
  textTransform: 'none',
  boxShadow: '0 1px 2px rgba(0, 30, 96, 0.08)',
  '&:hover': {
    borderColor: 'var(--primary-color)',
    backgroundColor: 'rgba(0, 30, 96, 0.05)',
  },
  '& .MuiButton-startIcon': { mr: 0.6 },
} as const;

const attachmentDeleteLinkSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.35,
  fontSize: '0.75rem',
  fontWeight: 600,
  minHeight: 44,
  px: 1,
  borderRadius: 1,
  '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.06)' },
} as const;

const AttachmentAddButton: React.FC<{
  uploading: boolean;
  disabled: boolean;
  accept: string;
  onSelect: (file: File) => void;
}> = ({ uploading, disabled, accept, onSelect }) => (
  <Button
    component="label"
    variant="outlined"
    size="small"
    disabled={disabled}
    startIcon={uploading ? <CircularProgress size={14} color="inherit" /> : <AddCircleOutlineIcon />}
    sx={attachmentAddButtonSx}
  >
    {uploading ? 'Adding…' : 'Add'}
    <input
      hidden
      type="file"
      accept={accept}
      onChange={(event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (file) onSelect(file);
      }}
    />
  </Button>
);

const InventoryAttachmentThumb: React.FC<{
  item: InventoryItem;
  att: InventoryAttachment;
  deleting: boolean;
  onDelete: () => void;
}> = ({ item, att, deleting, onDelete }) => {
  const href = buildInventoryAttachmentPreviewUrl(item, att);
  const image = isImageFilename(att.original_filename);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
      <Box
        component="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        title={att.original_filename}
        sx={{
          ...attachmentTileSx,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textDecoration: 'none',
          color: 'text.secondary',
          ...(image ? {} : { p: 0.5, '&:hover': { backgroundColor: '#f0f0f0' } }),
        }}
      >
        {image ? (
          <img
            src={href}
            alt={att.original_filename}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <InsertDriveFileOutlinedIcon sx={{ fontSize: 28, opacity: 0.85 }} />
            <Typography
              variant="caption"
              sx={{ fontSize: '0.65rem', fontWeight: 600, lineHeight: 1.1, mt: 0.25 }}
            >
              {fileExtensionLabel(att.original_filename)}
            </Typography>
          </Box>
        )}
      </Box>
      <Link
        component="button"
        type="button"
        color="error"
        underline="always"
        disabled={deleting}
        onClick={onDelete}
        aria-label={`Delete ${att.original_filename}`}
        sx={attachmentDeleteLinkSx}
      >
        <DeleteOutlineIcon sx={{ fontSize: 16 }} aria-hidden="true" />
        {deleting ? 'Deleting…' : 'Delete'}
      </Link>
    </Box>
  );
};

const InventoryList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const claimData = useSelector((state: RootState) => state.claim);
  const { showError, showSuccess } = useSnackbar();
  const { claimToken, fromClaimStatus, isResuming, resumeError, isClaimReady, isIncomplete } = useClaimTokenResume();

  const claimId = claimData.claimId;
  const incompleteFromStatus = fromClaimStatus && isIncomplete;

  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmittingClaim, setIsSubmittingClaim] = useState(false);
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);
  const [attachmentAction, setAttachmentAction] = useState<string | null>(null);
  const [claimSummary, setClaimSummary] = useState<{
    claimNumber: string;
    statusKey: string;
    statusLabel: string;
  } | null>(null);

  const [submitMode, setSubmitMode] = useState<'submit' | 'resubmit'>(isIncomplete ? 'resubmit' : 'submit');

  const buildInventoryPath = (path: string) =>
    buildClaimResumePath(path, claimToken, fromClaimStatus);

  const handleBack = () => {
    if (incompleteFromStatus && claimToken) {
      navigate(claimStatusReturnPath(claimToken));
      return;
    }
    if (submitMode === 'resubmit' && claimToken) {
      navigate(claimStatusReturnPath(claimToken));
      return;
    }
    navigate('/incident');
  };

  const handleHeaderSecondaryAction = () => {
    if (incompleteFromStatus && claimToken) {
      navigate(claimStatusReturnPath(claimToken));
      return;
    }
    navigate('/damages/saved');
  };

  useEffect(() => {
    if (isIncomplete) {
      setSubmitMode('resubmit');
    }
  }, [isIncomplete]);

  useEffect(() => {
    if (!isClaimReady || isResuming || claimId == null) {
      return;
    }

    const activeClaimId = claimId;

    const openAddIfEmpty = Boolean(
      (location.state as { openAddInventoryIfEmpty?: boolean } | null)?.openAddInventoryIfEmpty
    );

    const fetchItems = async () => {
      setIsLoadingItems(true);
      setErrorMessage('');
      try {
        const result = await getInventoryItems(activeClaimId);
        if (result.length === 0 && openAddIfEmpty) {
          navigate(buildClaimResumePath('/damages/add', claimToken, fromClaimStatus), { replace: true });
          return;
        }
        setItems(result);

        const claim = await getClaim(activeClaimId);
        const status = String(claim.status ?? '').toUpperCase();
        setSubmitMode(status === 'INCOMPLETE' ? 'resubmit' : 'submit');
        if (status === 'INCOMPLETE') {
          setClaimSummary({
            claimNumber: String(claim.claim_number ?? ''),
            statusKey: status.toLowerCase(),
            statusLabel: formatClaimStatusLabel(status),
          });
        } else {
          setClaimSummary(null);
        }
      } catch (error) {
        setErrorMessage(getErrorMessage(error) || 'Failed to load inventory items.');
        showError(getErrorMessage(error) || 'Failed to load inventory items.');
      } finally {
        setIsLoadingItems(false);
      }
    };
    fetchItems();
  }, [claimId, claimData.claimStatus, claimToken, fromClaimStatus, isClaimReady, isResuming, location.state, navigate, showError]);

  const handleDeleteItem = async (itemId: number) => {
    if (!claimId) return;
    const confirmed = window.confirm('Are you sure you want to delete this inventory item?');
    if (!confirmed) return;
    try {
      await deleteInventoryItem(claimId, itemId);
      setItems((prev) => prev.filter((item) => item.id !== itemId));
    } catch (error) {
      showError(getErrorMessage(error) || 'Failed to delete inventory item.');
    }
  };

  const handleAddAttachment = async (item: InventoryItem, file: File, type: InventoryAttachmentType) => {
    if (!claimId) return;

    const existing = (item.attachments || []).filter((attachment) => attachment.type === type);
    if (type === 'inventory_evidence' && existing.length >= 2) {
      showError('A maximum of 2 evidence files can be uploaded for each item.');
      return;
    }
    if (type === 'inventory_receipt' && existing.length > 0) {
      showError('Only one receipt can be uploaded for each item.');
      return;
    }

    setAttachmentAction(`upload-${item.id}-${type}`);
    try {
      // The API sets has_receipt / has_damage_evidence when the file is stored.
      const uploaded = await uploadInventoryAttachments(claimId, item.id, [file], type) as InventoryAttachment[];
      setItems((previous) =>
        previous.map((entry) =>
          entry.id === item.id
            ? {
                ...entry,
                attachments: [...(entry.attachments || []), ...uploaded],
                has_receipt: type === 'inventory_receipt' ? true : entry.has_receipt,
                has_damage_evidence: type === 'inventory_evidence' ? true : entry.has_damage_evidence,
              }
            : entry
        )
      );
      showSuccess(type === 'inventory_receipt' ? 'Receipt uploaded.' : 'Evidence uploaded.');
    } catch (error) {
      showError(getErrorMessage(error) || 'Failed to upload the file.');
    } finally {
      setAttachmentAction(null);
    }
  };

  const handleDeleteAttachment = async (item: InventoryItem, attachment: InventoryAttachment) => {
    if (!claimId) return;
    const confirmed = window.confirm(`Delete “${attachment.original_filename}”?`);
    if (!confirmed) return;

    setAttachmentAction(`delete-${attachment.id}`);
    try {
      // The API recomputes has_receipt / has_damage_evidence after the file is removed.
      await deleteInventoryAttachment(claimId, attachment.id);
      setItems((previous) =>
        previous.map((entry) => {
          if (entry.id !== item.id) return entry;
          const remaining = (entry.attachments || []).filter((entryAttachment) => entryAttachment.id !== attachment.id);
          return {
            ...entry,
            attachments: remaining,
            has_receipt: attachment.type === 'inventory_receipt'
              ? remaining.some((entryAttachment) => entryAttachment.type === 'inventory_receipt')
              : entry.has_receipt,
            has_damage_evidence: attachment.type === 'inventory_evidence'
              ? remaining.some((entryAttachment) => entryAttachment.type === 'inventory_evidence')
              : entry.has_damage_evidence,
          };
        })
      );
      showSuccess('File deleted.');
    } catch (error) {
      showError(getErrorMessage(error) || 'Failed to delete the file.');
    } finally {
      setAttachmentAction(null);
    }
  };

  const handleSubmitClaimClick = () => {
    if (!claimId) return;
    if (items.length === 0) {
      setErrorMessage('Please add at least one inventory item before submitting.');
      return;
    }
    setErrorMessage('');
    setSubmitConfirmOpen(true);
  };

  const handleSubmitClaimConfirm = async () => {
    if (!claimId) return;

    setIsSubmittingClaim(true);
    setErrorMessage('');
    try {
      if (submitMode === 'resubmit') {
        const token = claimToken || (await getClaim(claimId)).token;
        if (!token) {
          throw new Error('Claim token is missing.');
        }
        await resubmitIncompleteClaim(token);
        setSubmitConfirmOpen(false);
        showSuccess('Your claim has been re-submitted for review.');
        navigate(claimStatusReturnPath(token), { replace: true });
        return;
      }

      await updateClaim(claimId, { status: 'SUBMITTED' });
      try {
        await sendClaimConfirmation(claimId);
      } catch (emailError) {
        console.error(emailError);
      }
      navigate('/success');
    } catch (error) {
      setErrorMessage(getErrorMessage(error) || 'Failed to submit claim.');
      setSubmitConfirmOpen(false);
    } finally {
      setIsSubmittingClaim(false);
    }
  };

  if (!isClaimReady) {
    if (resumeError) {
      return (
        <Layout>
          <Box sx={{ maxWidth: 560, mx: 'auto', py: 6, px: 2 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {resumeError}
            </Alert>
            {claimToken && (
              <Button
                variant="contained"
                onClick={() => navigate(claimStatusReturnPath(claimToken))}
                sx={{ backgroundColor: 'var(--primary-color)' }}
              >
                Back to Claim Status
              </Button>
            )}
          </Box>
        </Layout>
      );
    }

    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={32} />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="banner">
        <img src={banner} alt="TPP Claims Banner" />
        <div className="banner-content">
          <h1>File a Claim</h1>
          <h2>Tenant Protection Plan</h2>
          <p>Follow our easy-to-use claim process to submit your claim!</p>
        </div>
      </div>

      <ProgressSteps currentStep="damages" hide={incompleteFromStatus} />

      <Box
        sx={{
          width: '100%',
          maxWidth: { md: 880, lg: 960 },
          mx: { md: 'auto' },
          px: { md: 0 },
          // Match Incident page: last `.form-row` has margin-bottom 1.5rem below the action buttons.
          pb: 3,
        }}
      >
        <Box sx={{ mb: 0 }}>
          {isIncomplete && claimSummary && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: { xs: 1.5, sm: 2 },
                mb: 2.5,
                p: { xs: 1.5, sm: 2 },
                borderRadius: 2.5,
                border: '1px solid var(--border-color)',
                background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
                boxShadow: '0 1px 4px rgba(0, 30, 96, 0.06)',
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mb: 0.5,
                    color: '#64748b',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                  }}
                >
                  Claim ID
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    m: 0,
                    fontWeight: 700,
                    color: 'var(--primary-color)',
                    lineHeight: 1.25,
                    wordBreak: 'break-word',
                  }}
                >
                  {claimSummary.claimNumber}
                </Typography>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mb: 0.5,
                    color: '#64748b',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                  }}
                >
                  Claim Status
                </Typography>
                <Typography
                  component="p"
                  className={`claim-status-badge claim-status-badge--${claimSummary.statusKey}`}
                  sx={{ m: 0, fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.25 }}
                >
                  {claimSummary.statusLabel}
                </Typography>
              </Box>
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: { sm: 'space-between' },
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: 2,
            }}
          >
            <Typography variant="h4" gutterBottom={false} sx={{ flex: { sm: '1 1 auto' }, minWidth: 0 }}>
              Damage Inventory of Items
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: 1.5,
                justifyContent: { xs: 'flex-end', sm: 'flex-end' },
                flexShrink: 0,
                alignSelf: { xs: 'stretch', sm: 'auto' },
              }}
            >
              <Button
                variant="outlined"
                onClick={() => navigate(buildInventoryPath('/damages/add'))}
                sx={inventoryHeaderButtonSx}
              >
                Add Items
              </Button>
              <Button
                variant="outlined"
                onClick={handleHeaderSecondaryAction}
                sx={inventoryHeaderButtonSx}
              >
                {incompleteFromStatus ? 'Back' : 'Save & Exit'}
              </Button>
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, mb: { xs: 3.5, md: 5 } }}>
            {incompleteFromStatus
              ? 'Your claim is incomplete. Add (or update) items one at a time. You can re-submit when you are done.'
              : 'Add inventory items one at a time. You can submit after you add all inventory items.'}
          </Typography>
        </Box>

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        {isLoadingItems ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={26} />
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ py: { xs: 3, md: 5 }, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" component="p" sx={{ m: 0 }}>
              No inventory items added yet.
            </Typography>
          </Box>
        ) : (
        <>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, md: 2 } }}>
          {items.map((item) => {
            const attachments = item.attachments || [];
            const evidenceAttachments = attachments.filter((a) => a.type === 'inventory_evidence');
            const receiptAttachments = attachments.filter((a) => a.type === 'inventory_receipt');

            return (
              <Box
                key={item.id}
                sx={{
                  border: '1px solid var(--border-color)',
                  borderRadius: 2.5,
                  p: { xs: 1.5, sm: 2 },
                  display: 'flex',
                  flexDirection: 'column',
                  gap: { xs: 1.5, sm: 2 },
                  backgroundColor: '#fff',
                  boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'stretch', sm: 'flex-start' },
                    gap: { xs: 1.5, sm: 2 },
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" sx={{ wordBreak: 'break-word', fontSize: '1rem', mb: 0.75 }}>
                      {item.item_description}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 0.5, whiteSpace: 'pre-wrap', fontStyle: 'italic' }}
                    >
                      Damage Description: {item.damage_description || 'Not provided'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Purchase Price: {formatInventoryPrice(item.price)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Purchase Date: {formatPurchase(item.date_of_purchase)}
                    </Typography>
                    
                  </Box>

                  <Box sx={{ flexShrink: 0, alignSelf: { xs: 'flex-end', sm: 'flex-start' }, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {submitMode === 'resubmit' && (
                      <Button
                        variant="outlined"
                        onClick={() => navigate(buildInventoryPath(`/damages/edit/${item.id}`))}
                        sx={{
                          fontWeight: 700,
                          minWidth: { xs: 92, sm: 84 },
                          borderColor: 'var(--primary-color)',
                          color: 'var(--primary-color)',
                        }}
                      >
                        Edit item
                      </Button>
                    )}
                    <Button
                      color="error"
                      variant="outlined"
                      sx={{
                        fontWeight: 700,
                        minWidth: { xs: 92, sm: 84 },
                        borderColor: 'rgba(211, 47, 47, 0.5)',
                        color: 'error.main',
                      }}
                      onClick={() => handleDeleteItem(item.id)}
                    >
                      Delete item
                    </Button>
                  </Box>
                </Box>

                <Box
                  sx={{
                    pt: { xs: 1, sm: 1.25 },
                    mt: { xs: 0, sm: 0 },
                    borderTop: '1px solid rgba(0,0,0,0.08)',
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 2, sm: 3 },
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, minHeight: 34 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        Evidence:
                      </Typography>
                      {evidenceAttachments.length < 2 && (
                        <AttachmentAddButton
                          uploading={attachmentAction === `upload-${item.id}-inventory_evidence`}
                          disabled={attachmentAction !== null}
                          accept="image/*,video/*,application/pdf,.doc,.docx"
                          onSelect={(file) => void handleAddAttachment(item, file, 'inventory_evidence')}
                        />
                      )}
                    </Box>
                    {evidenceAttachments.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        None
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {evidenceAttachments.map((att) => (
                          <InventoryAttachmentThumb
                            key={att.id}
                            item={item}
                            att={att}
                            deleting={attachmentAction === `delete-${att.id}`}
                            onDelete={() => void handleDeleteAttachment(item, att)}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, minHeight: 34 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        Receipt:
                      </Typography>
                      {receiptAttachments.length === 0 && (
                        <AttachmentAddButton
                          uploading={attachmentAction === `upload-${item.id}-inventory_receipt`}
                          disabled={attachmentAction !== null}
                          accept="image/*,application/pdf,.doc,.docx"
                          onSelect={(file) => void handleAddAttachment(item, file, 'inventory_receipt')}
                        />
                      )}
                    </Box>
                    {receiptAttachments.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        None
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {receiptAttachments.map((att) => (
                          <InventoryAttachmentThumb
                            key={att.id}
                            item={item}
                            att={att}
                            deleting={attachmentAction === `delete-${att.id}`}
                            onDelete={() => void handleDeleteAttachment(item, att)}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
        <Box sx={{ mt: { xs: 2, md: 2.5 } }}>
          <Link
            component={RouterLink}
            to={buildInventoryPath('/damages/add')}
            underline="always"
            sx={{
              fontWeight: 600,
              color: 'var(--primary-color)',
              textDecorationColor: 'var(--primary-color)',
              textUnderlineOffset: '0.22em',
              textDecorationThickness: 'max(2px, 0.075em)',
              '&:hover': {
                textDecorationThickness: 'max(3px, 0.09em)',
                textDecorationColor: 'var(--primary-color)',
              },
            }}
          >
            Add more items
          </Link>
        </Box>
        </>
        )}

        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          <Button
            variant="outlined"
            onClick={handleBack}
            fullWidth
            sx={{
              color: 'var(--primary-color)',
              borderColor: 'var(--primary-color)',
              fontWeight: 600,
              '&:hover': { backgroundColor: 'rgba(var(--primary-color-rgb), 0.04)' },
            }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            fullWidth
            onClick={handleSubmitClaimClick}
            disabled={isSubmittingClaim || submitConfirmOpen}
            sx={{ backgroundColor: 'var(--primary-color)', fontWeight: 700, '&:hover': { backgroundColor: 'var(--primary-color)' } }}
          >
            {isSubmittingClaim ? <CircularProgress size={22} /> : submitMode === 'resubmit' ? 'Re-submit Claim' : 'Submit Claim'}
          </Button>
        </Box>
      </Box>

      <SubmitClaimConfirmDialog
        open={submitConfirmOpen}
        onClose={() => setSubmitConfirmOpen(false)}
        onConfirm={handleSubmitClaimConfirm}
        isSubmitting={isSubmittingClaim}
        mode={submitMode}
      />
    </Layout>
  );
};

export default InventoryList;

